// Invite links on Analytics → Website: every link, sortable, with archive. Platform admins
// only (the API refuses everyone else; this card is not rendered for them either).
import { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, FormControlLabel, Stack, Switch, Typography } from '@mui/material'
import { ArchiveIcon, CopyIcon, EyeIcon, LinkIcon, PencilIcon } from 'lucide-react'
import { Button, Card, EMPTY_VALUE } from '@repo/ui'

import { ActionDropdown, ActivityTimeCell, ConfirmSettingModal, EmptyState, Table } from 'components'
import { useSnackbar } from 'contexts'
import { useTrackedLinkActions, useTrackedLinks, type TrackedLink, type TrackedLinksRange } from 'hooks/api/useTrackedLinks'
import { ROUTES } from 'routes'
import { getErrorMessage } from 'utils'

import { SectionNote, ToneChip } from '../InternalAnalyticsPage/components/shared'
import { CreateTrackedLinkModal } from './CreateTrackedLinkModal'
import { RenameTrackedLinkModal } from './RenameTrackedLinkModal'
import { interestSourceNote } from './TrackedLinkDetail'
import { TrackedLinkModal } from './TrackedLinkModal'

const cellSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

const Muted = ({ children }: { children?: React.ReactNode }) => (
  <Box sx={cellSx}>
    <Typography variant="body2" color={children ? 'text.secondary' : 'text.disabled'} noWrap>
      {children || EMPTY_VALUE}
    </Typography>
  </Box>
)

/** "For": the person, then the company under it. */
export const forLabel = (l: TrackedLink): string =>
  [l.person_label, l.company?.name ?? l.company_name].filter(Boolean).join(' · ')

export const TrackedLinksCard = ({
  createOpen,
  onCreateOpenChange,
  range,
  rangeLabel,
}: {
  createOpen: boolean
  onCreateOpenChange: (v: boolean) => void
  /** VRG-712 — Analytics → Website's date range: clicks and interests count only inside it. */
  range?: TrackedLinksRange
  /** How the page names that range ("7 days"), for the note. */
  rangeLabel?: string
}) => {
  const { open: notification } = useSnackbar()
  const [showArchived, setShowArchived] = useState(false)
  const { data, isLoading, error } = useTrackedLinks(true, showArchived, range)
  const { archive } = useTrackedLinkActions()
  const [detailId, setDetailId] = useState<string | null>(null)
  const [archiving, setArchiving] = useState<TrackedLink | null>(null)
  const [renaming, setRenaming] = useState<TrackedLink | null>(null)
  // The open detail reads the LIVE row, so a rename made while it is open shows in its title.
  const detail = useMemo(() => data?.links.find((l) => l.id === detailId) ?? null, [data, detailId])

  const copy = (url: string) =>
    navigator.clipboard.writeText(url).then(
      () => notification({ type: 'success', title: 'Link copied' }),
      () => notification({ type: 'error', title: 'Could not copy' }),
    )

  const columns = useMemo(
    () => [
      {
        field: 'label',
        headerName: 'Link',
        type: 'string',
        width: 260,
        minWidth: 220,
        flex: 260,
        sortable: true,
        disableColumnMenu: true,
        pinned: 'left',
        valueGetter: (_v: unknown, row: TrackedLink) => `${row.label} ${row.code}`,
        renderCell: ({ row }: { row: TrackedLink }) => (
          <Stack justifyContent="center" sx={{ height: '100%', minWidth: 0 }}>
            <Typography
              component="button"
              type="button"
              variant="body2"
              noWrap
              title={`${row.label}: view clicks`}
              onClick={() => setDetailId(row.id)}
              sx={{ p: 0, border: 0, bgcolor: 'transparent', color: 'text.primary', textAlign: 'left', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              {row.label}
            </Typography>
            <Typography variant="body3" color="text.secondary" noWrap title={row.url}>
              /invite/{row.code} → {row.destination}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'for',
        headerName: 'Person / company',
        type: 'string',
        width: 220,
        minWidth: 180,
        flex: 220,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: TrackedLink) => forLabel(row),
        renderCell: ({ row }: { row: TrackedLink }) => (
          <Stack justifyContent="center" sx={{ height: '100%', minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {row.person_label ?? EMPTY_VALUE}
            </Typography>
            {row.company ? (
              <Typography
                component={RouterLink}
                to={ROUTES.INTERNAL_COMPANY(row.company.id)}
                variant="body3"
                color="text.secondary"
                noWrap
                sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {row.company.name}
              </Typography>
            ) : row.company_name ? (
              <Typography variant="body3" color="text.secondary" noWrap>
                {row.company_name}
              </Typography>
            ) : null}
          </Stack>
        ),
      },
      {
        field: 'campaign',
        headerName: 'Campaign',
        type: 'string',
        width: 140,
        minWidth: 120,
        flex: 140,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: TrackedLink) => row.campaign ?? '',
        renderCell: ({ row }: { row: TrackedLink }) => <Muted>{row.campaign}</Muted>,
      },
      {
        field: 'clicks',
        headerName: 'Clicks',
        type: 'number',
        width: 110,
        minWidth: 104,
        flex: 110,
        sortable: true,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: TrackedLink }) => (
          <Box sx={cellSx} title={row.bot_clicks ? `${row.bot_clicks} more from bots and link previews, not counted` : undefined}>
            <Typography variant="body2">{row.clicks}</Typography>
          </Box>
        ),
      },
      {
        field: 'last_click_at',
        headerName: 'Last click',
        type: 'dateTime',
        width: 170,
        minWidth: 160,
        flex: 170,
        sortable: true,
        disableColumnMenu: true,
        // Sorts on the real Date; the cell is only the display.
        valueGetter: (_v: unknown, row: TrackedLink) => (row.last_click_at ? new Date(row.last_click_at) : null),
        // VRG-705 — the shared last-activity cell (first built here in VRG-703).
        renderCell: ({ row }: { row: TrackedLink }) => <ActivityTimeCell at={row.last_click_at} />,
      },
      {
        field: 'top_landing_path',
        headerName: 'Top landing page',
        type: 'string',
        width: 190,
        minWidth: 176,
        flex: 190,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: TrackedLink) => row.top_landing_path ?? '',
        renderCell: ({ row }: { row: TrackedLink }) => <Muted>{row.top_landing_path}</Muted>,
      },
      {
        field: 'top_interest',
        headerName: 'Top interest',
        type: 'string',
        width: 180,
        minWidth: 150,
        flex: 180,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: TrackedLink) => row.top_interest ?? '',
        renderCell: ({ row }: { row: TrackedLink }) => <Muted>{row.top_interest}</Muted>,
      },
      {
        field: 'actions',
        headerName: '',
        width: 54,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        disableReorder: true,
        pinned: 'right',
        renderCell: ({ row }: { row: TrackedLink }) => (
          <ActionDropdown
            items={[
              { label: 'Copy link', icon: <CopyIcon strokeWidth={1} />, onClick: () => void copy(row.url) },
              { label: 'View clicks', icon: <EyeIcon strokeWidth={1} />, onClick: () => setDetailId(row.id) },
              // VRG-794: the label only; the code (and the URL already sent) never changes.
              { label: 'Rename', icon: <PencilIcon strokeWidth={1} />, onClick: () => setRenaming(row) },
              {
                label: 'Archive',
                icon: <ArchiveIcon strokeWidth={1} />,
                destructive: true,
                divider: true,
                disabled: Boolean(row.archived_at),
                tooltip: row.archived_at ? 'Already archived' : undefined,
                onClick: () => setArchiving(row),
              },
            ]}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  )

  const note = interestSourceNote(data?.interestSource)

  return (
    <Card
      title="Tracked links"
      action={
        <Stack direction="row" spacing={3} alignItems="center">
          <ToneChip tone="neutral" label="Admins only" />
          <Button variant="secondary" size="small" startIcon={<LinkIcon strokeWidth={1} />} onClick={() => onCreateOpenChange(true)}>
            Create tracked link
          </Button>
        </Stack>
      }
    >
      <Stack spacing={3} sx={{ mt: 4 }}>
        <SectionNote>
          Links made for one person or company (vera.inc/invite/…). Clicks are counted for everyone, without cookies;
          bots and link previews are left out. Top interest is what they looked at after the click, for visitors whose analytics are
          on (accepted, or in the US not opted out).
          {range ? ` Clicks, last click and interests count the selected range${rangeLabel ? ` (${rangeLabel})` : ''}; every link is listed. A link's own window shows all time.` : ''}
        </SectionNote>
        {note && <SectionNote>{note}</SectionNote>}
        <FormControlLabel
          control={<Switch size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
          label={<Typography variant="body3">Show archived</Typography>}
        />
        {error ? (
          <SectionNote>Could not load tracked links. Reload, or check the API is running.</SectionNote>
        ) : (
          // The card gives the table a DEFINITE box and the table fills it (`fillParent`:
          // toolbar + grid as a flex column, grid `flex: 1`). It used to pass `height`, which
          // sizes the table's OUTER box — and the grid inside took that full height again
          // BELOW the toolbar, so the footer hung ~70px out of the card and "Visitor history"
          // overlapped it. `minWidth: 0` + `overflow: hidden` keep a wide grid scrolling
          // inside the card instead of widening it. Height: toolbar + header + footer, about
          // 72px a row, capped (the grid scrolls past the cap).
          <Box
            sx={{
              height: Math.min(700, 180 + Math.max(2, data?.links.length ?? 0) * 72),
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Table
              loading={isLoading}
              columns={columns}
              rows={data?.links ?? []}
              fillParent
              alwaysShowRowSelection={false}
              paginationMode="client"
              initialPageSize={25}
              viewKey="internal-tracked-links"
              emptyState={
                <EmptyState
                  title="No tracked links yet"
                  text="Create one here or from Access. Each link shows who clicked and what they looked at."
                  icon={<LinkIcon strokeWidth={1} size={32} />}
                />
              }
            />
          </Box>
        )}
      </Stack>

      <CreateTrackedLinkModal open={createOpen} onClose={() => onCreateOpenChange(false)} />

      <TrackedLinkModal
        link={detail ? { id: detail.id, label: detail.label, description: `${detail.url} → ${detail.destination}` } : null}
        onClose={() => setDetailId(null)}
      />

      <RenameTrackedLinkModal link={renaming} onClose={() => setRenaming(null)} />

      <ConfirmSettingModal
        open={Boolean(archiving)}
        onClose={() => setArchiving(null)}
        destructive
        loading={archive.isPending}
        title="Archive tracked link?"
        confirmLabel="Archive"
        consequence={
          <>
            <strong>{archiving?.url}</strong> stops counting clicks and sends visitors to the vera.inc home page. Its
            history stays.
          </>
        }
        onConfirm={() =>
          archiving &&
          archive.mutate(
            { id: archiving.id },
            {
              onSuccess: () => {
                setArchiving(null)
                notification({ type: 'success', title: 'Link archived' })
              },
              onError: (e) => notification({ type: 'error', title: getErrorMessage(e) }),
            },
          )
        }
      />
    </Card>
  )
}
